import { supabase, createSignedStorageUrl, logger } from '@/lib';
import { base64ToBlob } from './imageUtils';

/**
 * Admin profile avatars.
 *
 * Deliberately NOT a variant in OPTIMIZED_PHOTO_VARIANTS: the member webcam flow
 * (`useWebcamUpload`) calls `getAllVariantTypes()` and would then upload a phantom
 * avatar for every member photo. So this is a self-contained single-variant
 * pipeline.
 *
 * Stored variant: one square-bounded JPEG, longest side ≤ 320px, quality 0.90
 * (≈20–40 KB). The largest surface is the detail-card avatar (~96px); at 3× DPR
 * that needs ~288px, so 320px renders crisp everywhere while staying tiny. We
 * bound (preserve aspect) rather than center-crop; the round/cropped look is a
 * render-time concern handled by <AdminAvatar> via object-cover.
 */
export const ADMIN_AVATAR_BUCKET = 'admin-avatars';
const AVATAR_MAX_PX = 320;
const AVATAR_QUALITY = 0.9;

/** Stable per-admin object key. upsert:true means a re-upload replaces in place — no orphans. */
export function adminAvatarPath(adminId: string): string {
  return `${adminId}/avatar.jpg`;
}

/** Downscale a base64 image to the bounded 320px square-ish JPEG variant. */
function resizeToAvatar(base64Image: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('Failed to load image'));
    img.onload = () => {
      try {
        const longest = Math.max(img.width, img.height);
        const scale = longest > AVATAR_MAX_PX ? AVATAR_MAX_PX / longest : 1;
        // Round to even numbers for cleaner JPEG encoding.
        const width = Math.max(2, Math.round((img.width * scale) / 2) * 2);
        const height = Math.max(2, Math.round((img.height * scale) / 2) * 2);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', AVATAR_QUALITY));
      } catch (error) {
        reject(error);
      }
    };
    img.src = base64Image;
  });
}

export interface AdminAvatarUploadResult {
  path: string;
  signedUrl: string | null;
  expiresAt: number | null;
}

/**
 * Resize + upload an admin avatar to the private admin-avatars bucket.
 *
 * The DB row write (profile_photo_path) is NOT done here — admin_users has no
 * client-writable UPDATE policy; the caller persists the returned path via the
 * `update-admin-avatar` edge function. Storage writes are gated by the bucket's
 * super_admin policy, so only super-admins can reach this successfully.
 *
 * @param adminId target admin's id
 * @param base64Image source image as a data URL (from the file picker)
 */
export async function uploadAdminAvatar(
  adminId: string,
  base64Image: string,
): Promise<AdminAvatarUploadResult> {
  if (!adminId || !base64Image) {
    throw new Error('Invalid avatar upload parameters');
  }

  const resized = await resizeToAvatar(base64Image);
  const blob = base64ToBlob(resized);
  if (!blob || blob.size === 0) {
    throw new Error('Invalid avatar blob');
  }

  const path = adminAvatarPath(adminId);

  const { error: uploadError } = await supabase.storage
    .from(ADMIN_AVATAR_BUCKET)
    .upload(path, blob, { contentType: 'image/jpeg', upsert: true });

  if (uploadError) {
    logger.error('Error uploading admin avatar', uploadError, { adminId });
    throw uploadError;
  }

  const signed = await createSignedStorageUrl(ADMIN_AVATAR_BUCKET, path, 3600);
  return { path, signedUrl: signed.signedUrl, expiresAt: signed.expiresAt };
}

/** Remove an admin's avatar object from storage (best-effort; super_admin only via RLS). */
export async function removeAdminAvatar(adminId: string): Promise<void> {
  const { error } = await supabase.storage
    .from(ADMIN_AVATAR_BUCKET)
    .remove([adminAvatarPath(adminId)]);
  // A missing object is not an error worth surfacing — the goal state (no avatar)
  // is reached either way; the path-clear on admin_users is the source of truth.
  if (error) {
    logger.warn('Failed to remove admin avatar object (non-fatal)', { adminId, error }, 'adminAvatarUpload');
  }
}
